import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, Copy, Check, Wand2, Download, Upload, ImageIcon, Package, Archive, Send, Share2 } from "lucide-react";
import TagPicker from "@/components/studio/TagPicker";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "ig_feed", label: "Instagram Feed", ratio: "1:1" },
  { id: "ig_portrait", label: "Instagram Portrait", ratio: "4:5" },
  { id: "story", label: "Story / Reel", ratio: "9:16" },
  { id: "tiktok", label: "TikTok", ratio: "9:16" },
  { id: "fb_feed", label: "Facebook Feed", ratio: "1.9:1" },
  { id: "whatsapp", label: "WhatsApp", ratio: "1:1" },
];

const suggestPlatform = (ratio) => {
  const m = { "1:1": "ig_feed", "4:5": "ig_portrait", "9:16": "story", "1.9:1": "fb_feed", "2:1": "fb_feed" };
  return m[ratio] || "ig_feed";
};

export default function MarketingComposer({ asset, open, onOpenChange, onDone }) {
  const [src, setSrc] = useState(null); // {file_id, ratio, filename}
  const [pickerTab, setPickerTab] = useState("upload");
  const [platform, setPlatform] = useState("tiktok");
  const [pubLang, setPubLang] = useState("en");
  const [fit, setFit] = useState("fill");
  const [bg, setBg] = useState("#ffffff");
  const [headline, setHeadline] = useState("");
  const [subtext, setSubtext] = useState("");
  const [price, setPrice] = useState("");
  const [cta, setCta] = useState("");
  const [textPosition, setTextPosition] = useState("bottom");
  const [textColor, setTextColor] = useState("#ffffff");
  const [promo, setPromo] = useState("");
  const [caption, setCaption] = useState("");
  const [variants, setVariants] = useState([]);
  const [autoBusy, setAutoBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [buildBusy, setBuildBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [result, setResult] = useState(null);
  // source browsing
  const [bank, setBank] = useState([]);
  const [libFiles, setLibFiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [upFile, setUpFile] = useState(null);
  const [upBusy, setUpBusy] = useState(false);
  const [saveMk, setSaveMk] = useState(false);
  const [saveLib, setSaveLib] = useState(false);
  const [saveProd, setSaveProd] = useState(false);
  const [saveCat, setSaveCat] = useState(false);
  const [catId, setCatId] = useState("");
  const [catalogs, setCatalogs] = useState([]);
  const [upCategory, setUpCategory] = useState("");
  const [upTags, setUpTags] = useState([]);
  const [prodOpen, setProdOpen] = useState(false);
  const [prodCategory, setProdCategory] = useState("");
  const [prodTags, setProdTags] = useState(["Print file"]);
  const [prodBusy, setProdBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSrc(asset && asset.file_id ? { file_id: asset.file_id, ratio: asset.ratio, filename: asset.filename } : null);
      setPickerTab("upload"); setPlatform("tiktok"); setPubLang("en");
      setResult(null); setVariants([]); setCaption("");
      setHeadline(""); setSubtext(""); setPrice(""); setCta(""); setPromo("");
      setFit("fill"); setBg("#ffffff"); setTextPosition("bottom"); setTextColor("#ffffff");
      setUpFile(null); setSaveMk(false); setSaveLib(false); setSaveProd(false);
      setSaveCat(false); setCatId(""); setUpCategory(""); setUpTags([]);
      loadCatalogs();
    }
  }, [open, asset]);

  const loadBank = () => api.get("/marketing/assets", { params: { generated: "false" } }).then(({ data }) => setBank(data)).catch(() => {});
  const loadFiles = () => api.get("/library/files").then(({ data }) => setLibFiles(data)).catch(() => {});
  const loadProducts = () => api.get("/catalog-products").then(({ data }) => setProducts(data)).catch(() => {});
  const loadCatalogs = () => api.get("/catalogs").then(({ data }) => setCatalogs(data)).catch(() => {});

  const pickTab = (t) => { setPickerTab(t); if (t === "upload") loadCatalogs(); if (t === "bank") loadBank(); if (t === "files") loadFiles(); if (t === "products") loadProducts(); };

  const doUpload = async () => {
    if (!upFile) { toast.error("Pick a file"); return; }
    const anySave = saveMk || saveLib || saveProd || saveCat;
    if (anySave && (!upCategory.trim() || upTags.length === 0)) { toast.error("Category and at least one tag are required to save"); return; }
    if (saveCat && !catId) { toast.error("Pick a catalog"); return; }
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", upFile);
      fd.append("save_marketing", saveMk ? "true" : "false");
      fd.append("save_library", saveLib ? "true" : "false");
      fd.append("save_product", saveProd ? "true" : "false");
      fd.append("save_catalog", saveCat ? "true" : "false");
      fd.append("catalog_id", catId);
      fd.append("category", upCategory);
      fd.append("tags", upTags.join(","));
      const { data } = await api.post("/marketing/ingest", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSrc({ file_id: data.file_id, ratio: data.ratio, filename: data.filename });
      if (data.reused) toast.success("Reused existing file (no duplicate created)");
      else if (data.saved_to?.length) toast.success(`Uploaded & saved to: ${data.saved_to.join(", ")}`);
      else toast.success("Uploaded (source only — not saved)");
      setPlatform("tiktok");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setUpBusy(false); }
  };

  const pickProduct = async (p) => {
    try { const { data } = await api.post("/marketing/resolve-source", { product_id: p.id }); setSrc({ file_id: data.file_id, ratio: "", filename: p.name }); toast.success(`Using product: ${p.name}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not use product image"); }
  };

  const variantText = (v, lang) => `${lang === "es" ? v.es : v.en}\n\n${(v.hashtags || []).join(" ")}`.trim();

  const genCaptions = async (langForCaption) => {
    setGenBusy(true);
    try {
      const desc = promo || [headline, subtext, price].filter(Boolean).join(". ") || src?.filename || "a print shop product";
      const { data } = await api.post("/marketing/captions", { description: desc, platform, n: 3 });
      const vs = data.variants || [];
      setVariants(vs);
      if (vs.length) setCaption(variantText(vs[0], langForCaption || pubLang));
      return vs;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Caption generation failed"); return []; }
    finally { setGenBusy(false); }
  };

  const autoFillAll = async () => {
    if (!src) { toast.error("Choose a source image first"); return; }
    setAutoBusy(true);
    try {
      const { data } = await api.post("/marketing/post-autofill", { file_id: src.file_id, platform, lang: pubLang });
      if (data.headline) setHeadline(data.headline);
      if (data.subtext) setSubtext(data.subtext);
      if (data.cta) setCta(data.cta);
      if (data.promo) setPromo(data.promo);
      toast.success("Fields auto-filled — generating captions…");
      await genCaptions(pubLang);
      toast.success("Ready — review, correct & build");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Auto-fill failed"); }
    finally { setAutoBusy(false); }
  };

  const copy = async (text, key) => { try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch (e) {} };
  const switchLang = (lang) => { setPubLang(lang); if (variants.length) setCaption(variantText(variants[0], lang)); };

  const build = async () => {
    if (!src) { toast.error("Choose a source image first"); return; }
    setBuildBusy(true);
    try {
      const { data } = await api.post("/marketing/compose", {
        file_id: src.file_id, platform, fit, bg, headline, subtext, price, cta,
        text_position: textPosition, text_color: textColor, caption, promo, lang: pubLang, category: "Posts",
      });
      setResult(data);
      toast.success("Post created & saved");
      onDone && onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Build failed"); }
    finally { setBuildBusy(false); }
  };

  const copyForSocial = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(caption || "");
      const a = document.createElement("a");
      a.href = artUrl(result.file_id); a.download = result.filename || "post.jpg"; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Caption copied to clipboard · image downloaded — attach & paste");
    } catch (e) { toast.error("Could not copy"); }
  };

  const openProd = () => { if (!src) { toast.error("Choose a source first"); return; } setProdCategory(""); setProdTags(["Print file"]); setProdOpen(true); };
  const saveProduction = async () => {
    if (!prodCategory.trim() || prodTags.length === 0) { toast.error("Category and at least one tag are required"); return; }
    setProdBusy(true);
    try { const { data } = await api.post("/marketing/save-production", { file_id: src.file_id, label: src.filename, category: prodCategory, tags: prodTags }); toast.success(data.already ? "Already a print file — details updated" : "Saved as a print file in your Library"); setProdOpen(false); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not save"); }
    finally { setProdBusy(false); }
  };

  const langBtn = (id, label) => (
    <button onClick={() => switchLang(id)} className={`text-xs px-3 py-1.5 rounded-lg border font-semibold ${pubLang === id ? "border-[#2495D3] bg-[#2495D3] text-white" : "border-slate-200 text-slate-500"}`} data-testid={`composer-publang-${id}`}>{label}</button>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="composer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 size={18} /> Create post</DialogTitle>
          <DialogDescription>Choose a source (upload or pick a saved one), set the language, auto-fill with AI, then build & save.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Left: source + preview + platform */}
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 aspect-square flex items-center justify-center overflow-hidden" data-testid="composer-preview-box">
              {src || result ? (
                <img src={artUrl(result ? result.file_id : src.file_id)} alt="preview" className="w-full h-full object-contain" data-testid="composer-preview" />
              ) : (
                <div className="text-center text-slate-400 text-sm px-4"><ImageIcon size={30} className="mx-auto mb-2 opacity-50" />Choose a source image below</div>
              )}
            </div>
            {result && (
              <div className="grid grid-cols-2 gap-2">
                <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" download>
                  <Button variant="outline" size="sm" className="w-full" data-testid="composer-download"><Download size={14} className="mr-1.5" /> Download</Button>
                </a>
                <Button variant="outline" size="sm" onClick={copyForSocial} data-testid="composer-copy-social"><Share2 size={14} className="mr-1.5" /> Copy for WhatsApp/IG</Button>
              </div>
            )}
            {src && (
              <Button variant="ghost" size="sm" className="w-full text-xs" onClick={openProd} data-testid="composer-save-production"><Send size={13} className="mr-1.5" /> Save source as print file (production)</Button>
            )}

            {/* Source picker */}
            <div className="rounded-lg border border-slate-200 p-2">
              <div className="flex gap-1.5 mb-2" data-testid="composer-source-tabs">
                {[["upload", "Upload", Upload], ["bank", "Bank", Wand2], ["files", "Files", Archive], ["products", "Products", Package]].map(([id, l, Ico]) => (
                  <button key={id} onClick={() => pickTab(id)} className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${pickerTab === id ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500"}`} data-testid={`source-tab-${id}`}><Ico size={12} />{l}</button>
                ))}
              </div>
              {pickerTab === "upload" && (
                <div className="space-y-2" data-testid="source-upload">
                  <input type="file" accept="image/*,.pdf,.ai,.eps,.tiff" onChange={(e) => setUpFile(e.target.files?.[0] || null)} className="text-xs w-full" data-testid="source-file-input" />
                  <div className="flex flex-wrap gap-2 text-[11px] text-slate-600">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={saveMk} onChange={(e) => setSaveMk(e.target.checked)} data-testid="save-marketing" /> Bank</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={saveLib} onChange={(e) => setSaveLib(e.target.checked)} data-testid="save-library" /> Library</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={saveProd} onChange={(e) => setSaveProd(e.target.checked)} data-testid="save-product" /> Product</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={saveCat} onChange={(e) => setSaveCat(e.target.checked)} data-testid="save-catalog" /> Catalog</label>
                  </div>
                  {saveCat && (
                    <select value={catId} onChange={(e) => setCatId(e.target.value)} className="w-full h-8 text-xs rounded border px-2 bg-white" data-testid="save-catalog-select">
                      <option value="">Choose catalog…</option>{catalogs.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  )}
                  {(saveMk || saveLib || saveProd || saveCat) && (
                    <div className="space-y-1.5 rounded-md bg-amber-50 border border-amber-200 p-2" data-testid="save-meta">
                      <div className="text-[10px] text-amber-700 font-semibold">Category & tags required to save (no duplicate files are created)</div>
                      <Input value={upCategory} onChange={(e) => setUpCategory(e.target.value)} placeholder="Category *" className="h-8 text-xs" data-testid="save-category" />
                      <TagPicker value={upTags} onChange={setUpTags} testid="save-tags" />
                    </div>
                  )}
                  <Button size="sm" className="w-full" onClick={doUpload} disabled={upBusy} data-testid="source-upload-btn">{upBusy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Upload size={13} className="mr-1" />} Upload & use</Button>
                  <p className="text-[10px] text-slate-400 leading-tight">Uploads once (deduped by content). Check a box to also save it — no duplicate files.</p>
                </div>
              )}
              {pickerTab === "bank" && (
                <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto" data-testid="source-bank">
                  {bank.map((a) => <button key={a.id} onClick={() => setSrc({ file_id: a.file_id, ratio: a.ratio, filename: a.filename })} className="rounded border hover:border-[#2495D3] overflow-hidden" data-testid={`source-bank-${a.id}`}><img src={artUrl(a.file_id)} alt="" className="w-full aspect-square object-contain bg-slate-100" /></button>)}
                  {bank.length === 0 && <div className="col-span-4 text-xs text-slate-400 text-center py-4">Bank is empty.</div>}
                </div>
              )}
              {pickerTab === "files" && (
                <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto" data-testid="source-files">
                  {libFiles.filter((f) => (f.content_type || "").startsWith("image/")).map((f) => <button key={f.file_id} onClick={() => setSrc({ file_id: f.file_id, ratio: "", filename: f.filename })} className="rounded border hover:border-[#2495D3] overflow-hidden" data-testid={`source-file-${f.file_id}`}><img src={artUrl(f.file_id)} alt="" className="w-full aspect-square object-contain bg-slate-100" /></button>)}
                  {libFiles.length === 0 && <div className="col-span-4 text-xs text-slate-400 text-center py-4">No library files.</div>}
                </div>
              )}
              {pickerTab === "products" && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="source-products">
                  {products.filter((p) => p.image_url).map((p) => <button key={p.id} onClick={() => pickProduct(p)} className="w-full flex items-center gap-2 rounded border p-1.5 hover:border-[#2495D3] text-left" data-testid={`source-product-${p.id}`}><img src={p.image_url} alt="" className="w-9 h-9 object-contain rounded bg-slate-100" /><span className="text-xs truncate">{p.name}</span></button>)}
                  {products.filter((p) => p.image_url).length === 0 && <div className="text-xs text-slate-400 text-center py-4">No products with images.</div>}
                </div>
              )}
            </div>

            <div>
              <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Platform (★ = suggested by ratio)</label>
              <div className="flex flex-wrap gap-1.5" data-testid="composer-platforms">
                {PLATFORMS.map((p) => (
                  <button key={p.id} onClick={() => setPlatform(p.id)}
                    className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors ${platform === p.id ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    data-testid={`composer-platform-${p.id}`}>{p.label} <span className="opacity-60">{p.ratio}</span>{src?.ratio && suggestPlatform(src.ratio) === p.id ? " ★" : ""}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Fit</label>
                <div className="flex gap-1.5">
                  {["fill", "fit"].map((f) => (
                    <button key={f} onClick={() => setFit(f)} className={`text-xs px-3 py-1.5 rounded-lg border ${fit === f ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500"}`} data-testid={`composer-fit-${f}`}>{f === "fill" ? "Fill (crop)" : "Fit (bars)"}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Background</label>
                <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-9 w-12 rounded border" data-testid="composer-bg" />
              </div>
            </div>
          </div>

          {/* Right: language + auto-fill + text + captions */}
          <div className="space-y-3">
            <div className="rounded-lg bg-[#2495D3]/5 border border-[#2495D3]/20 p-2.5 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-mono uppercase text-slate-600">Publish language</span>
                <div className="flex gap-1.5" data-testid="composer-publang">{langBtn("en", "English")}{langBtn("es", "Español")}</div>
              </div>
              <Button size="sm" onClick={autoFillAll} disabled={autoBusy || genBusy || !src} className="w-full bg-[#2495D3] hover:bg-[#1E7AA9]" data-testid="composer-autofill">
                {autoBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Auto-fill everything with AI
              </Button>
              <p className="text-[10px] text-slate-500 leading-tight">Generates both languages; caption & overlay use the selected language. Your edits train future suggestions.</p>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Input placeholder="Headline (optional)" value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="composer-headline" />
              <Input placeholder="Subtext (optional)" value={subtext} onChange={(e) => setSubtext(e.target.value)} data-testid="composer-subtext" />
              <div className="flex gap-2">
                <Input placeholder="Price e.g. $19.99" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="composer-price" />
                <Input placeholder="CTA e.g. Order now" value={cta} onChange={(e) => setCta(e.target.value)} data-testid="composer-cta" />
              </div>
              <div className="flex gap-2 items-center">
                <select value={textPosition} onChange={(e) => setTextPosition(e.target.value)} className="h-9 rounded-lg border px-2 text-sm flex-1 bg-white" data-testid="composer-text-position">
                  <option value="top">Text top</option>
                  <option value="center">Text center</option>
                  <option value="bottom">Text bottom</option>
                </select>
                <label className="text-[11px] text-slate-500 flex items-center gap-1">Text<input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="h-9 w-10 rounded border" data-testid="composer-text-color" /></label>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <Input placeholder="What are you promoting? (for better AI captions)" value={promo} onChange={(e) => setPromo(e.target.value)} className="mb-2" data-testid="composer-promo" />
              <Button size="sm" variant="outline" onClick={() => genCaptions()} disabled={genBusy} className="w-full" data-testid="composer-gen-captions">
                {genBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Regenerate captions (ES + EN)
              </Button>
              <div className="space-y-2 mt-2 max-h-52 overflow-y-auto" data-testid="composer-variants">
                {variants.map((v, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-2 text-xs space-y-1" data-testid={`composer-variant-${i}`}>
                    <div className="flex items-start gap-1"><span className="font-mono text-[9px] bg-slate-100 rounded px-1 mt-0.5">ES</span><span className="flex-1">{v.es}</span>
                      <button onClick={() => copy(variantText(v, "es"), `es${i}`)} className="text-slate-400 hover:text-[#2495D3]">{copied === `es${i}` ? <Check size={12} /> : <Copy size={12} />}</button></div>
                    <div className="flex items-start gap-1"><span className="font-mono text-[9px] bg-slate-100 rounded px-1 mt-0.5">EN</span><span className="flex-1">{v.en}</span>
                      <button onClick={() => copy(variantText(v, "en"), `en${i}`)} className="text-slate-400 hover:text-[#2495D3]">{copied === `en${i}` ? <Check size={12} /> : <Copy size={12} />}</button></div>
                    {(v.hashtags || []).length > 0 && <div className="text-[10px] text-[#2495D3]">{(v.hashtags || []).join(" ")}</div>}
                    <button onClick={() => setCaption(variantText(v, pubLang))} className="text-[10px] text-emerald-600 hover:underline font-semibold" data-testid={`composer-use-${i}`}>Use this caption ({pubLang.toUpperCase()})</button>
                  </div>
                ))}
              </div>
              {caption && (
                <>
                  <div className="text-[10px] font-mono uppercase text-slate-400 mt-2">Caption to publish ({pubLang.toUpperCase()})</div>
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} className="w-full mt-1 text-xs rounded-lg border p-2" data-testid="composer-caption" />
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={build} disabled={buildBusy || !src} data-testid="composer-build">
            {buildBusy ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Wand2 size={15} className="mr-1.5" />} Build & save post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={prodOpen} onOpenChange={setProdOpen}>
      <DialogContent className="max-w-sm" data-testid="production-dialog">
        <DialogHeader>
          <DialogTitle>Save as print file</DialogTitle>
          <DialogDescription>Category and tags are required so production files stay organized.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input value={prodCategory} onChange={(e) => setProdCategory(e.target.value)} placeholder="Category * (e.g. Banners)" data-testid="production-category" />
          <div className="text-[11px] font-mono uppercase text-slate-400">Tags *</div>
          <TagPicker value={prodTags} onChange={setProdTags} testid="production-tags" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setProdOpen(false)}>Cancel</Button>
          <Button onClick={saveProduction} disabled={prodBusy} data-testid="production-save">{prodBusy ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Send size={15} className="mr-1.5" />} Save print file</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
